import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import { PM_PROVIDERS } from 'common/constants/constants';
import { getPMIssuesType } from 'common/services/pm.service';
import {
  getCustomFieldsItems,
  getJiraItems,
  getShortcutItems,
  getAzureBoardsItems,
  getIssuesEntities,
  getPdtProjects,
  getJiraIssueTypesItems,
  getDynamicIssuesEntities
} from './FilterMenus.service';
import { FILTER_TYPES } from 'common/constants/filters';
import useSettingsService from 'components/hooks/useSettingsService';
import DynamicIssueTypes from './components/DynamicIssueTypes';

export const SUPPORTED_PM_FILTERS = {
  [PM_PROVIDERS.JIRA]: {
    [FILTER_TYPES.PROJECTS]: {
      id: FILTER_TYPES.PROJECTS,
      title: 'Jira Projects',
      isPaginated: true,
      getItems: getJiraItems,
      onSearch: getJiraItems
    },
    [FILTER_TYPES.BOARD]: {
      id: FILTER_TYPES.BOARD,
      title: 'Jira Board',
      isPaginated: true,
      getItems: getJiraItems,
      onSearch: getJiraItems
    },
    [FILTER_TYPES.EPIC]: {
      title: 'Epic',
      id: FILTER_TYPES.EPIC,
      isPaginated: true,
      disabled: false,
      isWithIcon: true,
      getItems: getJiraItems,
      onSearch: getJiraItems
    },
    [FILTER_TYPES.LABEL]: {
      title: 'Label',
      id: FILTER_TYPES.LABEL,
      isPaginated: true,
      disabled: false,
      isWithIcon: true,
      getItems: getJiraItems,
      onSearch: getJiraItems
    },
    [FILTER_TYPES.FIELD]: {
      title: 'Field',
      id: FILTER_TYPES.FIELD,
      isPaginated: true,
      disabled: false,
      isWithIcon: true,
      multiSelected: false, // in case we want custom fields to be with same behavior as other entities (without inserting free text value) - config it as true
      getItems: getCustomFieldsItems,
      onSearch: getCustomFieldsItems
    },
    [FILTER_TYPES.FIX_VERSION]: {
      id: FILTER_TYPES.FIX_VERSION,
      title: 'Fix Version',
      featureFlag: FEATURE_FLAGS_NAMES.projectSettingsFixVersionEnabled,
      isPaginated: true,
      getItems: getJiraItems,
      onSearch: getJiraItems
    },
    [FILTER_TYPES.ORGANIZATION_TEAMS]: {
      id: FILTER_TYPES.ORGANIZATION_TEAMS,
      title: 'LinearB Team'
    },
    [FILTER_TYPES.ORGANIZATION_PDT]: {
      // to use it you have to send withEntityPayload prop
      id: FILTER_TYPES.ORGANIZATION_PDT,
      title: 'LinearB Project',
      getItems: getPdtProjects,
      onSearch: getPdtProjects
    },
    [FILTER_TYPES.ISSUE_TYPES]: {
      id: FILTER_TYPES.ISSUE_TYPES,
      title: 'Issue Types',
      isPaginated: true,
      getItems: getJiraIssueTypesItems,
      onSearch: getJiraIssueTypesItems
    },
    [FILTER_TYPES.KEYWORDS]: {
      id: FILTER_TYPES.KEYWORDS,
      title: 'Keywords',
      header: 'Select keywords',
      isFreeInputs: true
    },
    [FILTER_TYPES.ISSUE_PARENT_2]: {
      id: FILTER_TYPES.ISSUE_PARENT_2,
      title: 'Initiative',
      header: 'Select Initiative',
      isPaginated: true,
      getItems: getIssuesEntities,
      onSearch: getIssuesEntities
    },
    [FILTER_TYPES.ISSUE_PARENT_1]: {
      id: FILTER_TYPES.ISSUE_PARENT_1,
      title: 'Epic',
      header: 'Select Epic',
      isPaginated: true,
      getItems: getIssuesEntities,
      onSearch: getIssuesEntities
    },
    [FILTER_TYPES.DYNAMIC_ISSUES]: {
      id: FILTER_TYPES.DYNAMIC_ISSUES,
      header: 'Select Issues',
      isPaginated: true,
      getItems: getDynamicIssuesEntities,
      onSearch: getDynamicIssuesEntities,
      Component: DynamicIssueTypes,
      extraPayloadPicker: ['entity_type'],
      ignoreLocalSearch: true
    }
  },
  [PM_PROVIDERS.CLUBHOUSE]: {
    [FILTER_TYPES.PROJECTS]: {
      title: 'Shortcut Project',
      id: FILTER_TYPES.PROJECTS,
      getItems: getShortcutItems,
      onSearch: getShortcutItems
    },
    [FILTER_TYPES.TEAMS]: {
      title: 'Team',
      id: FILTER_TYPES.TEAMS,
      getItems: getShortcutItems,
      onSearch: getShortcutItems
    },
    [FILTER_TYPES.EPIC]: {
      title: 'Epic',
      id: FILTER_TYPES.EPIC,
      getItems: getShortcutItems,
      onSearch: getShortcutItems
    },
    [FILTER_TYPES.LABEL]: {
      title: 'Label',
      id: FILTER_TYPES.LABEL,
      getItems: getShortcutItems,
      onSearch: getShortcutItems
    },
    [FILTER_TYPES.ISSUE_TYPES]: {
      id: FILTER_TYPES.ISSUE_TYPES,
      title: 'Issue Types',
      getItems: async () => {
        const { items } = await getPMIssuesType();
        return items.map(({ id, name }) => ({ id, title: name }));
      },
      onSearch: async ({ keyword = '' }) => {
        const { items } = await getPMIssuesType();
        return items
          .map(({ id, name }) => ({ id, title: name }))
          .filter(({ title }) =>
            title.toLowerCase().includes(keyword?.toLowerCase())
          );
      }
    }
  },
  [PM_PROVIDERS.AZURE_BOARDS]: {
    [FILTER_TYPES.LABEL]: {
      id: FILTER_TYPES.LABEL,
      title: 'Tag',
      header: `Select Tag`,
      isPaginated: true,
      getItems: getAzureBoardsItems,
      onSearch: getAzureBoardsItems
    },
    [FILTER_TYPES.ISSUE_TYPES]: {
      id: FILTER_TYPES.ISSUE_TYPES,
      title: 'Work Item',
      isPaginated: true,
      getItems: getAzureBoardsItems,
      onSearch: getAzureBoardsItems
    },
    [FILTER_TYPES.FIELD]: {
      title: 'Field',
      id: FILTER_TYPES.FIELD,
      disabled: false,
      isWithIcon: true,
      multiSelected: false, // in case we want custom fields to be with same behavior as other entities (without inserting free text value) - config it as true
      isPaginated: true,
      getItems: getCustomFieldsItems,
      onSearch: getCustomFieldsItems
    },
    [FILTER_TYPES.EPIC]: {
      title: 'Feature',
      id: FILTER_TYPES.EPIC,
      isPaginated: true,
      getItems: getAzureBoardsItems,
      onSearch: getAzureBoardsItems
    },
    [FILTER_TYPES.ORGANIZATION_TEAMS]: {
      id: FILTER_TYPES.ORGANIZATION_TEAMS,
      title: 'LinearB Team'
    },
    [FILTER_TYPES.KEYWORDS]: {
      id: FILTER_TYPES.KEYWORDS,
      title: 'Keywords',
      header: 'Select keywords',
      isFreeInputs: true
    },
    [FILTER_TYPES.ISSUE_PARENT_2]: {
      id: FILTER_TYPES.ISSUE_PARENT_2,
      title: 'Epic',
      header: 'Select Epic',
      isPaginated: true,
      getItems: getIssuesEntities,
      onSearch: getIssuesEntities
    },
    [FILTER_TYPES.ISSUE_PARENT_1]: {
      id: FILTER_TYPES.ISSUE_PARENT_1,
      title: 'Feature',
      header: 'Select Feature',
      isPaginated: true,
      getItems: getIssuesEntities,
      onSearch: getIssuesEntities
    },
    [FILTER_TYPES.PROJECTS]: {
      id: FILTER_TYPES.PROJECTS,
      title: 'Projects',
      isPaginated: true,
      getItems: getAzureBoardsItems,
      onSearch: getAzureBoardsItems
    },
    [FILTER_TYPES.DYNAMIC_ISSUES]: {
      id: FILTER_TYPES.DYNAMIC_ISSUES,
      header: 'Select Issues',
      isPaginated: true,
      getItems: getDynamicIssuesEntities,
      onSearch: getDynamicIssuesEntities,
      Component: DynamicIssueTypes,
      extraPayloadPicker: ['entity_type'],
      ignoreLocalSearch: true
    }
  },
  config(pmProvider, filterType, payload) {
    if (this[pmProvider][filterType]) {
      this[pmProvider][filterType] = {
        ...this[pmProvider][filterType],
        ...payload
      };
    }
    return this;
  }
};

/**
 * To use pmFiltersBuilder you need to send object of arrays of desired filter types for each provider
 * You also must specify getSettingOrDefinitionValue
 * example:
 * {
 *  [PM_PROVIDERS.JIRA]: [FILTER_TYPES.BOARD, FILTER_TYPES.EPIC, FILTER_TYPES.LABEL],
 *  [PM_PROVIDERS.CLUBHOUSE]: [FILTER_TYPES.SHORTCUT_LABEL, FILTER_TYPES.SHORTCUT_EPICS],
 *  [PM_PROVIDERS.AZURE_BOARDS]: [FILTER_TYPES.AZURE_BOARDS_PROJECT_TEAM_AREAS],
 * },
 * getSettingOrDefinitionValue
 * if we are not supporting Shortcut at all for example, don't provide it in the object
 */

export const pmFiltersBuilder = (
  filterTypesObj,
  getSettingOrDefinitionValue
) => {
  if (!getSettingOrDefinitionValue) {
    throw new Error(
      `You must specify 'getSettingOrDefinitionValue' to 'pmFiltersBuilder'`
    );
  }
  const filters = Object.entries(filterTypesObj).reduce(
    (acc, [provider, filterTypes]) => {
      if (!SUPPORTED_PM_FILTERS[provider]) {
        throw new Error(`Provider '${provider}' is not supported`);
      }
      acc[provider] = {};
      filterTypes.forEach(filterType => {
        const currentFilter = SUPPORTED_PM_FILTERS[provider][filterType];
        if (!currentFilter) {
          throw new Error(
            `Filter '${filterType}' does not exist for provider '${provider}'`
          );
        }
        if (currentFilter.featureFlag) {
          const currentFeaturFlag = getSettingOrDefinitionValue({
            name: currentFilter.featureFlag
          });
          if (!currentFeaturFlag) return;
        }
        acc[provider][filterType] = currentFilter;
      });
      return acc;
    },
    {}
  );
  filters['config'] = SUPPORTED_PM_FILTERS.config;
  return filters;
};

export const usePmFiltersBuilder = (filterTypesObj = {}) => {
  const { getSettingOrDefinitionValue } = useSettingsService();

  const filters = pmFiltersBuilder(filterTypesObj, getSettingOrDefinitionValue);
  return filters;
};
